import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, CheckCircle, ArrowRight, Star, CreditCard, GraduationCap, Truck, Users, Zap, DollarSign, MapPin, HeadphonesIcon, Award, Shield } from 'lucide-react';
import { heroImages, services, whyChooseUs, howItWorks, testimonials, companyInfo } from '../data/mock';

const HomePage = () => {
  const iconMap = {
    CreditCard,
    GraduationCap,
    Truck,
    Users,
    Zap,
    DollarSign,
    MapPin,
    HeadphonesIcon,
    Award,
    Shield
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-construction-darkblue text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src={heroImages.main} 
            alt="Construction workers" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-construction-darkblue via-construction-darkblue/90 to-transparent"></div>
        </div>

        <div className="container mx-auto px-4 py-20 md:py-32 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-block bg-construction-yellow text-construction-darkblue px-4 py-2 rounded-full text-sm font-bold mb-6 animate-in fade-in slide-in-from-bottom">
              🔥 Fast Track Your Construction Career
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Get Your <span className="text-construction-yellow">CSCS & CPCS Cards</span> Fast – Book Today
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Don't risk being turned away from site. Get qualified quickly with expert support and competitive prices.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <a
                href={`tel:${companyInfo.phone}`}
                className="inline-flex items-center justify-center gap-2 bg-construction-yellow text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-all hover:scale-105 shadow-lg"
              >
                <Phone size={24} />
                Call Now
              </a>
              <a
                href={`https://wa.me/${companyInfo.whatsapp.replace(/\+/g, '')}?text=Hi, I'm interested in your construction card services`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-green-700 transition-all hover:scale-105 shadow-lg"
              >
                <MessageCircle size={24} />
                WhatsApp Us
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-white text-construction-darkblue border-2 border-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all hover:scale-105 shadow-lg"
              >
                Apply Online
              </Link>
            </div>

            <div className="flex flex-wrap gap-6 text-sm md:text-base">
              <div className="flex items-center gap-2">
                <CheckCircle className="text-construction-yellow flex-shrink-0" size={20} />
                <span>Same Day Support</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="text-construction-yellow flex-shrink-0" size={20} />
                <span>95% Pass Rate</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="text-construction-yellow flex-shrink-0" size={20} />
                <span>Nationwide Service</span>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Bottom Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Services Overview - Enhanced */}
      <section className="py-20 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-72 h-72 bg-construction-yellow rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-construction-darkblue rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-block bg-construction-yellow/10 px-6 py-2 rounded-full mb-4">
              <span className="text-construction-darkblue font-bold text-sm">⭐ PROFESSIONAL SERVICES</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-construction-darkblue mb-6">
              Fast-Track Your <span className="text-construction-yellow">Construction Career</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Get qualified quickly with our expert-led training and certification services across the UK
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <Link
                  key={service.id}
                  to={service.link}
                  className={`group relative bg-white rounded-2xl overflow-hidden transition-all duration-500 hover:scale-105 ${
                    service.featured 
                      ? 'shadow-2xl shadow-construction-yellow/20 ring-4 ring-construction-yellow/30' 
                      : 'shadow-lg hover:shadow-2xl'
                  }`}
                  style={{
                    animationDelay: `${index * 100}ms`
                  }}
                >
                  {/* Gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-construction-yellow/0 via-construction-yellow/0 to-construction-yellow/0 group-hover:from-construction-yellow/5 group-hover:via-transparent group-hover:to-construction-darkblue/5 transition-all duration-500"></div>

                  {/* Popular badge */}
                  {service.featured && (
                    <div className="absolute -top-2 -right-2 z-20">
                      <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2 rounded-bl-2xl rounded-tr-2xl shadow-lg animate-pulse">
                        <span className="text-xs font-bold flex items-center gap-1">
                          🔥 MOST POPULAR
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Card content */}
                  <div className="relative p-8">
                    {/* Animated icon container */}
                    <div className="relative mb-6">
                      <div className="absolute inset-0 bg-construction-yellow/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500 group-hover:scale-110"></div>
                      <div className="relative bg-gradient-to-br from-construction-yellow to-yellow-500 w-20 h-20 rounded-2xl flex items-center justify-center transform group-hover:rotate-6 transition-all duration-500 shadow-lg">
                        <Icon className="text-construction-darkblue" size={36} />
                      </div>
                      {/* Corner accent */}
                      <div className="absolute -top-2 -right-2 w-8 h-8 border-t-4 border-r-4 border-construction-yellow/30 rounded-tr-xl"></div>
                    </div>

                    {/* Service title */}
                    <h3 className="text-xl font-bold text-construction-darkblue mb-3 group-hover:text-construction-yellow transition-colors duration-300">
                      {service.title}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-600 mb-6 leading-relaxed text-sm">
                      {service.shortDesc}
                    </p>

                    {/* Benefits list */}
                    <ul className="space-y-2 mb-6">
                      {service.benefits.slice(0, 3).map((benefit, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                          <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={16} />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <span className="text-construction-yellow font-bold text-sm group-hover:gap-3 flex items-center transition-all">
                        Learn More 
                        <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                      </span>
                      <div className="w-10 h-10 rounded-full bg-construction-yellow/10 group-hover:bg-construction-yellow flex items-center justify-center transition-all duration-300">
                        <ArrowRight className="text-construction-darkblue" size={20} />
                      </div>
                    </div>
                  </div>

                  {/* Bottom accent line */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-construction-yellow to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                </Link>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-16">
            <p className="text-gray-600 mb-6 text-lg">
              Not sure which service you need? <strong>We'll help you choose!</strong>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-construction-yellow text-construction-darkblue px-8 py-4 rounded-xl font-bold text-lg hover:bg-yellow-500 transition-all hover:scale-105 shadow-xl hover:shadow-2xl"
              >
                Get Free Consultation
                <ArrowRight size={20} />
              </Link>
              <a
                href={`tel:${companyInfo.phone}`}
                className="inline-flex items-center justify-center gap-2 bg-construction-darkblue text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-black transition-all hover:scale-105 shadow-xl hover:shadow-2xl"
              >
                <Phone size={20} />
                Call {companyInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-construction-darkblue text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Simple 3-step process to get your construction cards
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {howItWorks.map((step, index) => (
              <div key={step.step} className="relative">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 h-full border border-white/20">
                  <div className="bg-construction-yellow text-construction-darkblue w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mb-4 mx-auto">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-center">
                    {step.title}
                  </h3>
                  <p className="text-gray-300 text-center leading-relaxed">
                    {step.description}
                  </p>
                </div>
                {index < howItWorks.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="text-construction-yellow" size={32} />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-construction-yellow text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-all hover:scale-105"
            >
              Get Started Now
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-4">
              Why Choose Us?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Trusted by thousands of construction workers across the UK
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-construction-yellow/10 p-3 rounded-lg flex-shrink-0">
                      <Icon className="text-construction-yellow" size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-construction-darkblue mb-2">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Real feedback from construction workers who got their cards with us
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:border-construction-yellow hover:shadow-lg transition-all"
              >
                <div className="flex gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="text-construction-yellow fill-current" size={18} />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  "{testimonial.text}"
                </p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-bold text-construction-darkblue">
                    {testimonial.name}
                  </p>
                  <p className="text-sm text-gray-600">
                    {testimonial.role} - {testimonial.location}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {testimonial.date}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-construction-yellow">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-4">
            Don't Risk Being Turned Away From Site
          </h2>
          <p className="text-xl text-construction-darkblue/80 mb-8 max-w-2xl mx-auto">
            Get your CSCS or CPCS card today and secure your construction career
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-construction-darkblue text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-black transition-all hover:scale-105 shadow-lg"
            >
              <Phone size={24} />
              Call {companyInfo.phone}
            </a>
            <a
              href={`https://wa.me/${companyInfo.whatsapp.replace(/\+/g, '')}?text=Hi, I want to apply for my construction cards`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-green-700 transition-all hover:scale-105 shadow-lg"
            >
              <MessageCircle size={24} />
              WhatsApp
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all hover:scale-105 shadow-lg"
            >
              Apply Online Form
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
